import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { pharmacyAPI, patientAPI } from '../../utils/api';
import { toast } from 'react-toastify';
import useDebounce from '../../utils/useDebounce';

const SOURCES = ['OPD', 'IPD', 'Emergency', 'Walk-in'];
const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

export default function PharmacyDispense() {
    const navigate = useNavigate();
    const [patientSearch, setPatientSearch] = useState('');
    const [patients, setPatients] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [source, setSource] = useState('OPD');
    const [visitRef, setVisitRef] = useState('');
    const [notes, setNotes] = useState('');

    const [drugSearch, setDrugSearch] = useState('');
    const [drugs, setDrugs] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(null);

    const debouncedPatient = useDebounce(patientSearch, 400);
    const debouncedDrug = useDebounce(drugSearch, 400);

    useEffect(() => {
        if (debouncedPatient.length < 2) { setPatients([]); return; }
        patientAPI.search(debouncedPatient).then(r => setPatients(r.data.patients || [])).catch(() => {});
    }, [debouncedPatient]);

    useEffect(() => {
        if (debouncedDrug.length < 2) { setDrugs([]); return; }
        pharmacyAPI.getDrugs({ q: debouncedDrug }).then(r => setDrugs(r.data)).catch(() => {});
    }, [debouncedDrug]);

    const searchPatients = useCallback((q) => setPatientSearch(q), []);
    const searchDrugs = useCallback((q) => setDrugSearch(q), []);

    const addItem = (drug) => {
        if (items.find(i => i.drug_id === drug.id)) { toast.warning('Drug already added'); return; }
        if (parseInt(drug.stock_qty) <= 0) { toast.error(`${drug.name} is out of stock`); return; }
        setItems(prev => [...prev, {
            drug_id: drug.id,
            drug_name: `${drug.name} ${drug.strength || ''}`.trim(),
            unit: drug.unit,
            quantity: 1,
            dose_instructions: '',
            unit_price: parseFloat(drug.sale_price) || 0,
            total_price: parseFloat(drug.sale_price) || 0,
            stock_qty: parseInt(drug.stock_qty),
        }]);
        setDrugSearch('');
        setDrugs([]);
    };

    const updateItem = (idx, field, val) => {
        setItems(prev => prev.map((item, i) => {
            if (i !== idx) return item;
            const updated = { ...item, [field]: val };
            updated.total_price = (parseFloat(updated.unit_price) || 0) * (parseInt(updated.quantity) || 0);
            return updated;
        }));
    };

    const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

    const totalAmount = items.reduce((s, i) => s + (i.total_price || 0), 0);

    const handleDispense = async () => {
        if (!selectedPatient) return toast.error('Select a patient');
        if (items.length === 0) return toast.error('Add at least one drug');
        setLoading(true);
        try {
            const res = await pharmacyAPI.dispense({
                patient_id: selectedPatient.id,
                prescription_source: source,
                visit_ref: visitRef,
                notes,
                items: items.map(i => ({
                    drug_id: i.drug_id, drug_name: i.drug_name, quantity: parseInt(i.quantity),
                    unit: i.unit, dose_instructions: i.dose_instructions,
                    unit_price: parseFloat(i.unit_price) || 0,
                })),
            });
            toast.success(`Dispensed — ${res.data.dispensing_number}`);
            setDone(res.data);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Dispense failed');
        } finally { setLoading(false); }
    };

    const handlePrint = () => {
        const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
        <style>
            body { font-family: Arial, sans-serif; padding: 20px; font-size: 13px; }
            .header { text-align: center; border-bottom: 2px solid #1e3a5f; padding-bottom: 10px; margin-bottom: 14px; }
            .header h2 { margin: 0; font-size: 18px; color: #1e3a5f; }
            .header p { margin: 2px 0; font-size: 12px; color: #64748b; }
            .rx-symbol { font-size: 32px; color: #1e3a5f; font-style: italic; font-weight: bold; }
            .info { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 14px; font-size: 12px; }
            .info span { color: #64748b; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
            th { background: #1e3a5f; color: white; padding: 7px 10px; text-align: left; font-size: 12px; }
            td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; font-size: 12px; }
            .total { text-align: right; font-size: 14px; font-weight: bold; color: #1e3a5f; }
            .footer { margin-top: 20px; font-size: 10px; color: #94a3b8; text-align: center; border-top: 1px solid #e2e8f0; padding-top: 10px; }
            .sig { margin-top: 30px; display: flex; justify-content: space-between; font-size: 11px; }
            @media print { @page { size: A5; margin: 10mm; } }
        </style></head><body>
        <div class="header">
            <div class="rx-symbol">℞</div>
            <h2>HIMS Pharmacy</h2>
            <p>Prescription Slip — ${done.dispensing_number}</p>
            <p>${new Date().toLocaleString('en-PK')}</p>
        </div>
        <div class="info">
            <div><span>Patient:</span> <strong>${selectedPatient.full_name}</strong></div>
            <div><span>MRN:</span> <strong>${selectedPatient.mrn}</strong></div>
            <div><span>Age/Gender:</span> ${selectedPatient.age_years ? selectedPatient.age_years + 'y' : '—'} / ${selectedPatient.gender || '—'}</div>
            <div><span>Source:</span> ${source}</div>
        </div>
        <table>
            <tr><th>#</th><th>Medicine</th><th>Qty</th><th>Instructions</th><th>Amount</th></tr>
            ${items.map((item, i) => `
            <tr>
                <td>${i + 1}</td>
                <td><strong>${item.drug_name}</strong></td>
                <td>${item.quantity} ${item.unit}</td>
                <td>${item.dose_instructions || '—'}</td>
                <td>Rs. ${item.total_price.toFixed(2)}</td>
            </tr>`).join('')}
        </table>
        <div class="total">Total: Rs. ${totalAmount.toFixed(2)}</div>
        ${notes ? `<p style="font-size:12px; color:#475569; margin-top:10px;"><strong>Notes:</strong> ${notes}</p>` : ''}
        <div class="sig">
            <div>Patient/Attendant Signature: ___________</div>
            <div>Pharmacist Signature: ___________</div>
        </div>
        <div class="footer">HIMS — Hospital Information Management System — Keep this slip for your records</div>
        </body></html>`;
        const win = window.open('', '_blank', 'width=700,height=600');
        if (!win) return alert('Popup blocked!');
        win.document.write(html);
        win.document.close();
        setTimeout(() => win.print(), 600);
    };

    if (done) {
        return (
            <div style={{ maxWidth: 500, margin: '60px auto', textAlign: 'center' }}>
                <div style={{ fontSize: 60, marginBottom: 16 }}>✅</div>
                <h2 style={{ fontSize: 22, color: '#059669', marginBottom: 8 }}>Dispensed Successfully</h2>
                <p style={{ fontSize: 14, color: '#64748b', marginBottom: 4 }}>Rx Number: <strong>{done.dispensing_number}</strong></p>
                <p style={{ fontSize: 18, fontWeight: 800, color: '#1e3a5f', marginBottom: 24 }}>Total: Rs. {parseFloat(done.total_amount).toFixed(2)}</p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                    <button onClick={handlePrint}
                        style={{ padding: '10px 24px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        🖨️ Print Slip
                    </button>
                    <button onClick={() => { setDone(null); setSelectedPatient(null); setItems([]); setPatientSearch(''); setNotes(''); setVisitRef(''); }}
                        style={{ padding: '10px 24px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        + New Dispense
                    </button>
                    <button onClick={() => navigate('/pharmacy')}
                        style={{ padding: '10px 24px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <button onClick={() => navigate('/pharmacy')} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13 }}>← Back</button>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e3a5f', margin: 0 }}>💊 Dispense Medicine</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Issue prescription to patient</p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                {/* Left — Patient & Rx info */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Patient search */}
                    <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: 18 }}>
                        <h4 style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', margin: '0 0 12px' }}>Patient</h4>
                        {selectedPatient ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f0fdf4', borderRadius: 8, padding: '10px 14px', border: '1px solid #bbf7d0' }}>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 13 }}>{selectedPatient.full_name}</div>
                                    <div style={{ fontSize: 12, color: '#64748b' }}>{selectedPatient.mrn} · {selectedPatient.age_years}y · {selectedPatient.gender}</div>
                                </div>
                                <button onClick={() => { setSelectedPatient(null); setPatientSearch(''); }}
                                    style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 18 }}>×</button>
                            </div>
                        ) : (
                            <div style={{ position: 'relative' }}>
                                <input value={patientSearch} onChange={e => { setPatientSearch(e.target.value); searchPatients(e.target.value); }}
                                    placeholder="Search by name, MRN, CNIC..."
                                    style={{ ...inputStyle, paddingRight: 36 }} />
                                {patients.length > 0 && (
                                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, zIndex: 100, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                                        {patients.map(p => (
                                            <div key={p.id} onClick={() => { setSelectedPatient(p); setPatients([]); }}
                                                style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                                                <div style={{ fontWeight: 600 }}>{p.full_name}</div>
                                                <div style={{ fontSize: 11, color: '#64748b' }}>{p.mrn} · {p.age_years}y · {p.gender}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Rx Info */}
                    <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: 18 }}>
                        <h4 style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', margin: '0 0 12px' }}>Prescription Info</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <div>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Source</label>
                                <select style={{ ...inputStyle, background: 'white' }} value={source} onChange={e => setSource(e.target.value)}>
                                    {SOURCES.map(s => <option key={s}>{s}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Visit / Admission Ref (optional)</label>
                                <input style={inputStyle} value={visitRef} onChange={e => setVisitRef(e.target.value)} placeholder="OPD visit no. or IPD admission no." />
                            </div>
                            <div>
                                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Notes</label>
                                <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Additional instructions..." />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right — Drug selection */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: 18 }}>
                        <h4 style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', margin: '0 0 12px' }}>Add Medicines</h4>
                        <div style={{ position: 'relative', marginBottom: 14 }}>
                            <input value={drugSearch} onChange={e => { setDrugSearch(e.target.value); searchDrugs(e.target.value); }}
                                placeholder="Type drug name to search..."
                                style={inputStyle} />
                            {drugs.length > 0 && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, zIndex: 100, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                                    {drugs.map(d => (
                                        <div key={d.id} onClick={() => addItem(d)}
                                            style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                                            <div style={{ fontWeight: 600 }}>{d.name} {d.strength}</div>
                                            <div style={{ fontSize: 11, color: parseInt(d.stock_qty) === 0 ? '#dc2626' : '#64748b' }}>
                                                {d.generic_name} · Stock: {d.stock_qty} {d.unit}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {items.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '30px 0', color: '#94a3b8', fontSize: 13 }}>No drugs added yet</div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {items.map((item, idx) => (
                                    <div key={idx} style={{ background: '#f8fafc', borderRadius: 8, padding: 12, border: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                            <span style={{ fontWeight: 700, fontSize: 13, color: '#1e3a5f' }}>{item.drug_name}</span>
                                            <button onClick={() => removeItem(idx)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16 }}>×</button>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                                            <div>
                                                <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>Qty ({item.unit})</label>
                                                <input type="number" min="1" style={{ ...inputStyle, padding: '5px 8px' }} value={item.quantity}
                                                    onChange={e => updateItem(idx, 'quantity', e.target.value)} />
                                            </div>
                                            <div>
                                                <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>Unit Price</label>
                                                <input type="number" step="0.01" style={{ ...inputStyle, padding: '5px 8px' }} value={item.unit_price}
                                                    onChange={e => updateItem(idx, 'unit_price', e.target.value)} />
                                            </div>
                                            <div>
                                                <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>Total</label>
                                                <div style={{ padding: '6px 8px', fontWeight: 700, fontSize: 13, color: '#059669' }}>Rs. {(item.total_price || 0).toFixed(2)}</div>
                                            </div>
                                        </div>
                                        <div style={{ marginTop: 8 }}>
                                            <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>Dose Instructions</label>
                                            <input style={{ ...inputStyle, padding: '5px 8px' }} value={item.dose_instructions}
                                                onChange={e => updateItem(idx, 'dose_instructions', e.target.value)}
                                                placeholder="e.g. 1 tablet morning/evening after meal" />
                                        </div>
                                    </div>
                                ))}

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1e3a5f', borderRadius: 8, padding: '12px 16px', marginTop: 4 }}>
                                    <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 600 }}>Total Amount</span>
                                    <span style={{ color: 'white', fontSize: 18, fontWeight: 800 }}>Rs. {totalAmount.toFixed(2)}</span>
                                </div>

                                <button onClick={handleDispense} disabled={loading}
                                    style={{ width: '100%', padding: '12px', background: loading ? '#9ca3af' : '#059669', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', marginTop: 4 }}>
                                    {loading ? 'Processing...' : '✅ Confirm Dispense'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
